import type {
  HealthReport,
  HistoryLevelInfo,
  PublicConfig,
  PublicQuiz,
  QuizAttemptResult,
  QuizCounts,
  QuizLevel,
  QuizQuestion,
  QuizSummary,
  VideoHistoryItem,
  VideoHistoryPage,
  VideoMode,
  VideoStatus,
  VideoSummary,
  VideoLevelStatus,
  StageTiming,
} from "@lib/domain";
import type { DiagnosticsLog } from "@lib/diagnostics";
import { sliceQuizPool } from "@lib/quizSlice";
import { apiError } from "@/lib/errors";
import { parseYouTubeId } from "@/lib/url";

const MOCK_ELAPSED_TO_READY_MS = 10_500;
/** Level 1 is generated on its own and is served well before L2/L3. */
const MOCK_L1_READY_MS = 8_000;
const MOCK_POOL_SIZE = 20;
const MOCK_TEXT_MIN_WORDS = 50;
const MOCK_TEXT_WORDS_FREE = 2000;
const MOCK_TEXT_WORDS_PAID = 10000;
const MOCK_TEXT_WORDS_ACCEPTED = 100000;
let MOCK_PAYWALL = false;
let MOCK_REQUIRE_PREVIOUS_PASS = false;

const STAGES: readonly { untilMs: number; status: VideoStatus; progress: number }[] = [
  { untilMs: 2_500, status: "queued", progress: 4 },
  { untilMs: 5_000, status: "fetching_transcript", progress: 30 },
  { untilMs: 7_500, status: "formatting_transcript", progress: 55 },
  { untilMs: 10_500, status: "generating_questions", progress: 82 },
];

interface MockJob {
  videoId: string;
  mode: VideoMode;
  createdAt: number;
  options?: QuizCounts;
  /** Text-input quizzes have no YouTube metadata; this is the snippet title. */
  title?: string;
  /** Optional display channel for seeded browse fixtures. */
  channel?: string;
  /** Set once a browser/paste transcript is supplied, so the job reads ready. */
  transcriptSubmitted?: boolean;
  /** Dev fixture: force a machine-readable failure state (e.g. needs-browser). */
  errorCode?: string | null;
  errorMessage?: string | null;
  /** Text-mode source was longer than the tier budget and got trimmed. */
  truncated?: boolean;
}

interface MockQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  scope?: "video" | "beyond";
}

const JOBS = new Map<string, MockJob>();
const ATTEMPTS = new Map<string, number>();

const MOCK_MODEL = "openai/gpt-4o-mini";
const DEFAULT_COUNTS: QuizCounts = { 1: 5, 2: 8, 3: 6 };

/** Curated seed questions per level, padded to a full MOCK_POOL_SIZE pool. */
const SEED_QUESTIONS: Record<QuizLevel, MockQuestion[]> = {
  1: [
    {
      question: "What does the Cloudflare network route requests to when you deploy a Worker?",
      options: [
        "A single origin server you configure",
        "The nearest of hundreds of data centers around the world",
        "A regional load balancer per continent",
        "A centralized cloud in the US East region",
      ],
      correctIndex: 1,
      explanation:
        "Workers execute at the edge — in the data center closest to the visitor — which is what makes the model so fast.",
    },
    {
      question: "Which JavaScript runtime do Cloudflare Workers run on?",
      options: ["V8", "Node.js", "Deno", "JSCore"],
      correctIndex: 0,
      explanation:
        "Workers use a hardened V8-based runtime, keeping startup and latency extremely low.",
    },
    {
      question: "How is a Worker priced on the free tier?",
      options: [
        "Always free, unlimited requests",
        "A flat monthly fee plus per-request charges",
        "Pay-as-you-go with a generous free daily request quota",
        "Free only for static assets",
      ],
      correctIndex: 2,
      explanation:
        "Workers offer a daily free request allocation before any overage, which suits low-traffic apps perfectly.",
    },
    {
      question: "What Cloudflare storage product pairs with Workers for simple key-value data?",
      options: ["D1 (relational)", "R2 (object storage)", "Hyperdrive", "Workers KV"],
      correctIndex: 3,
      explanation:
        "Workers KV is a global key-value store, great for config, feature flags, and fast cached reads.",
    },
    {
      question: "What does the D1 database provide compared to KV?",
      options: [
        "Only cached reads with no writes",
        "A relational SQL database with transactions",
        "Serverless static file hosting",
        "A queueing system for background jobs",
      ],
      correctIndex: 1,
      explanation:
        "D1 is a serverless SQLite-compatible relational database supporting transactions and SQL.",
    },
  ],
  2: [
    {
      question:
        "If traffic to your Worker suddenly spikes globally, what behavior would you observe compared to a monolithic server?",
      options: [
        "Requests queue at a single bottleneck",
        "Workload scales across nearby edge locations",
        "Users must wait for a cold reboot",
        "Global latency increases evenly",
      ],
      correctIndex: 1,
      explanation:
        "Edge execution spreads load across data centers, absorbing spikes closer to users.",
    },
    {
      question: "A read-heavy app needs fast, eventually-consistent lookups. Which pairing is most appropriate?",
      options: [
        "Workers + D1 for every request",
        "Workers + KV for cached reads",
        "A VM with a local file",
        "Serverless functions calling an external API each time",
      ],
      correctIndex: 1,
      explanation:
        "KV is built for high-read, low-latency key-value workloads rather than relational queries.",
    },
    {
      question: "Why would you choose D1 for transactional order processing over KV?",
      options: [
        "D1 supports ACID transactions and joins",
        "D1 is faster for single key lookups",
        "KV stores relational data natively",
        "KV has built-in SQL",
      ],
      correctIndex: 0,
      explanation:
        "D1's SQLite-compatible engine provides transactions and relational queries KV cannot.",
    },
    {
      question: "What happens to a Worker's cold start relative to a containerized Node server?",
      options: [
        "Workers start much faster on average",
        "Workers start slower after traffic scales",
        "They are identical",
        "Containers always start instantly",
      ],
      correctIndex: 0,
      explanation:
        "The lightweight, pre-warmed isolates of Workers avoid heavy container boot.",
    },
    {
      question: "Your free tier begins failing with overage errors. What is the most likely cause?",
      options: [
        "Exceeding the daily request allowance",
        "Missing secrets on the Worker",
        "Using KV instead of D1",
        "Choosing a slower runtime",
      ],
      correctIndex: 0,
      explanation:
        "The free daily allocation has a cap; sustained traffic beyond it triggers overage.",
    },
    {
      question: "How would you design a Worker that caches rarely-changing config globally?",
      options: [
        "Query D1 on every request",
        "Store it in KV and read at the edge",
        "Embed it only at build time",
        "Fetch it from an origin service per request",
      ],
      correctIndex: 1,
      explanation:
        "KV serves config reads at the edge with very low latency and no per-request origin call.",
    },
    {
      question: "Why might KV reads still serve slightly stale config after an update?",
      options: [
        "KV propagates changes asynchronously",
        "KV has no write path",
        "D1 shadows KV writes",
        "Workers refuse cache reads",
      ],
      correctIndex: 0,
      explanation:
        "KV invalidates/cache-propagates asynchronously, so eventual consistency applies.",
    },
    {
      question: "Which statement about combining Workers with managed Cloudflare products is TRUE?",
      options: [
        "You must deploy each product in a separate project",
        "Bindings let Workers access storage and config directly",
        "Only D1 can be bound",
        "Bindings require a paid plan",
      ],
      correctIndex: 1,
      explanation:
        "Bindings are first-class connections from a Worker to KV, D1, R2 and more.",
    },
  ],
  3: [
    {
      question: "How does edge computing change capacity planning compared to traditional cloud?",
      options: [
        "You provision peak capacity up front",
        "Demand-driven scaling happens near the user",
        "You must overbuy per region",
        "Latency is irrelevant to planning",
      ],
      correctIndex: 1,
      explanation:
        "Beyond the video: edge scales per-location on demand, shifting planning from provisioned to elastic.",
      scope: "beyond",
    },
    {
      question: "Which trade-off best describes KV's consistency model?",
      options: [
        "Strong consistency, low availability",
        "Eventual consistency for global reads",
        "Serializable transactions everywhere",
        "No write consistency",
      ],
      correctIndex: 1,
      explanation:
        "Beyond the video: KV optimizes for read latency, accepting eventual consistency.",
      scope: "beyond",
    },
    {
      question: "For the Workers storage family, which product is designed to be the source of truth?",
      options: ["Workers KV", "D1", "Hyperdrive", "Edge Cache"],
      correctIndex: 1,
      explanation:
        "Beyond the video: D1 is a durable relational store; KV is optimized as a read cache layer.",
      scope: "beyond",
    },
    {
      question: "Which feature separates Workers' model from typical FaaS offerings like AWS Lambda?",
      options: [
        "Requests execute in a single cold region",
        "Code is bundled and replicated across hundreds of locations",
        "Only static assets run on the edge",
        "Execution always stays in one data center",
      ],
      correctIndex: 1,
      explanation:
        "Workers run code in the nearest of hundreds of edge locations, unlike region-centric FaaS.",
    },
    {
      question: "What type of scaling model does the free tier's request quota represent?",
      options: [
        "Unlimited calls",
        "A daily usage allowance with overage beyond",
        "A total monthly hard stop with no overage",
        "Per-request billing only",
      ],
      correctIndex: 1,
      explanation:
        "Free tier grants a daily allowance; sustained excess triggers overage charges.",
    },
    {
      question: "Which real-world workload is a poor fit for KV as the primary database?",
      options: [
        "Reading a feature flag",
        "Lookups of per-user profile fields by email",
        "Monthly multi-join analytics reports",
        "Caching a globally read document",
      ],
      correctIndex: 2,
      explanation:
        "Beyond the video: relational aggregation queries need SQL/D1, not key-value KV.",
      scope: "beyond",
    },
  ],
};

/** Deterministic filler questions so every level exposes the full 20-question pool. */
function synthQuestion(level: QuizLevel, n: number): MockQuestion {
  const labels = ["Recap", "Understand", "Beyond"];
  const topic =
    level === 1
      ? ["Worker requests run at the edge", "D1 is transactional", "KV caches reads", "Free tier has a daily quota", "Isolates start fast"][(n - 1) % 5]!
      : level === 2
        ? ["Edge scaling absorbs spikes", "KV suits read-heavy loads", "Bindings connect storage", "Cold starts are rare on Workers", "D1 supports joins"][(n - 1) % 5]!
        : ["Edge reduces latency", "KV is eventually consistent", "D1 is relational", "Serverless scales elastically", "Capacity planning shifts to demand"][(n - 1) % 5]!;
  const beyond = level === 3 && n % 2 === 0;
  return {
    question: `Mock ${labels[level - 1]} ${n}: ${topic} — which statement follows?`,
    options: [
      "Statement A about the video",
      "Statement B about the video",
      "Statement C about the video",
      "Statement D about the video",
    ],
    correctIndex: (n % 4),
    explanation: beyond
      ? "Beyond the video: this synthesizes adjacent knowledge for the demo pool."
      : "This synthesizes a question on the point named in the stem.",
    scope: beyond ? "beyond" : undefined,
  };
}

function poolFor(level: QuizLevel): QuizQuestion[] {
  const seeded = SEED_QUESTIONS[level];
  const rows: MockQuestion[] =
    seeded.length >= MOCK_POOL_SIZE
      ? seeded
      : [
          ...seeded,
          ...Array.from({ length: MOCK_POOL_SIZE - seeded.length }, (_, i) =>
            synthQuestion(level, seeded.length + i + 1),
          ),
        ];
  return rows.map((q, i) => ({ ...q, id: `pool${level}_${i + 1}` }));
}

function attemptKey(videoId: string, level: QuizLevel): string {
  return `${videoId}:${level}`;
}

function bestPercent(videoId: string, level: QuizLevel): number | null {
  const best = ATTEMPTS.get(attemptKey(videoId, level));
  return best === undefined ? null : best;
}

function countsFor(job: MockJob): QuizCounts {
  return { ...DEFAULT_COUNTS, ...(job.options ?? {}) };
}

function levelStatuses(job: MockJob, level1Ready: boolean, ready: boolean): VideoLevelStatus[] {
  const counts = countsFor(job);
  return [1, 2, 3].map((level) => {
    // Only Level 1 exists until the Understand + Beyond call finishes.
    const available = level === 1 ? level1Ready : ready;
    const prevPassed = level === 1 || (bestPercent(job.videoId, (level - 1) as QuizLevel) ?? -1) >= 70;
    const best = bestPercent(job.videoId, level as QuizLevel);
    return {
      level: level as QuizLevel,
      name: ["Recap", "Understand", "Beyond"][level - 1]!,
      tagline: ["Straight from the video", "Apply what you learned", "Go further than the video"][level - 1]!,
      questionCount: available ? counts[level as QuizLevel] : null,
      available,
      unlocked: available && (!MOCK_REQUIRE_PREVIOUS_PASS || prevPassed),
      bestPercent: best,
      passed: (best ?? -1) >= 70,
    };
  });
}

/** Representative stage durations so the dev fixture matches the real summary. */
function mockTimings(job: MockJob, level1Ready: boolean, ready: boolean): StageTiming[] | undefined {
  if (!level1Ready) return undefined;
  const at = (ms: number) => new Date((job.createdAt + ms) * 1000).toISOString();
  const rows: StageTiming[] = [
    { stage: "transcript", durationMs: 1200, model: null, status: "ok", detail: { segmentCount: 20 }, createdAt: at(1200) },
    { stage: "format", durationMs: 300, model: null, status: "ok", detail: { chars: 4800 }, createdAt: at(1500) },
    { stage: "generate_level_1", durationMs: 6300, model: MOCK_MODEL, status: "ok", detail: { poolSize: MOCK_POOL_SIZE }, createdAt: at(8000) },
  ];
  if (ready) {
    rows.push({
      stage: "generate_levels_23",
      durationMs: 5000,
      model: MOCK_MODEL,
      status: "ok",
      detail: { poolSize: MOCK_POOL_SIZE },
      createdAt: at(10500),
    });
    rows.push({ stage: "total", durationMs: 10500, model: null, status: "ok", detail: null, createdAt: at(10500) });
  }
  return rows;
}

function buildSummary(job: MockJob, elapsedMs: number): VideoSummary {
  const failed = Boolean(job.errorCode);
  const instant = !failed && (job.mode === "text" || job.transcriptSubmitted === true);
  const ready = !failed && (instant || elapsedMs >= MOCK_ELAPSED_TO_READY_MS);
  const level1Ready = !failed && (instant || elapsedMs >= MOCK_L1_READY_MS);
  const stage = STAGES.find((s) => elapsedMs < s.untilMs);
  const progress = failed || ready ? 100 : (stage?.progress ?? 100);
  const activeStatus: VideoStatus = failed ? "failed" : ready ? "ready" : (stage?.status ?? "queued");
  const quiz: QuizSummary | null =
    ready || level1Ready
      ? {
          id: `q_${job.videoId}`,
          level: 1,
          questionCount: countsFor(job)[1],
          model: MOCK_MODEL,
          createdAt: new Date(job.createdAt + MOCK_L1_READY_MS).toISOString(),
        }
      : null;

  return {
    videoId: job.videoId,
    status: activeStatus,
    stage: failed ? "failed" : ready ? null : activeStatus,
    mode: job.mode,
    error: failed
      ? (job.errorMessage ??
        "Automatic transcript fetch is blocked from our servers. Fetch the transcript from your browser to build the quiz.")
      : null,
    errorCode: job.errorCode ?? null,
    truncated: job.truncated ?? false,
    title: job.title ?? "Cloudflare Workers: Serverless at the Edge",
    channel: job.mode === "text" ? null : "Cloudflare",
    thumbnail: job.mode === "text" ? null : `https://i.ytimg.com/vi/${job.videoId}/hqdefault.jpg`,
    durationSeconds: job.mode === "text" ? null : 1560,
    progress,
    createdAt: new Date(job.createdAt * 1000).toISOString(),
    updatedAt: new Date(job.createdAt + elapsedMs).toISOString(),
    quiz,
    levels: levelStatuses(job, level1Ready, ready),
    timings: mockTimings(job, level1Ready, ready),
  };
}

function requireJob(videoId: string): MockJob {
  const job = JOBS.get(videoId);
  if (!job) {
    throw apiError(404, "NOT_FOUND", `No video found with id "${videoId}".`);
  }
  return job;
}

function requireLevelReady(job: MockJob, level: QuizLevel): void {
  const threshold = level === 1 ? MOCK_L1_READY_MS : MOCK_ELAPSED_TO_READY_MS;
  if (Date.now() - job.createdAt < threshold) {
    throw apiError(
      409,
      "QUIZ_NOT_READY",
      level === 1 ? "The quiz is still being prepared. Try again soon." : `Level ${level} is still being prepared.`,
    );
  }
}

function requireUnlocked(job: MockJob, level: QuizLevel): void {
  if (level > 1 && (bestPercent(job.videoId, (level - 1) as QuizLevel) ?? -1) < 70) {
    throw apiError(403, "QUIZ_LOCKED", "Pass the previous level to unlock this one.", { level });
  }
}

function servedQuestions(job: MockJob, level: QuizLevel): { questions: QuizQuestion[]; count: number } {
  const count = countsFor(job)[level];
  const sliced = sliceQuizPool(poolFor(level), count, level);
  return { questions: sliced.questions, count: sliced.served };
}

export async function mockCreateVideo(
  url: string,
  mode: VideoMode,
  questionCounts?: QuizCounts,
): Promise<VideoSummary> {
  const videoId = parseYouTubeId(url);
  if (!videoId) {
    throw apiError(
      400,
      "INVALID_URL",
      "That doesn't look like a valid YouTube URL or 11-character video ID.",
    );
  }
  const createdAt = Date.now();
  JOBS.set(videoId, { videoId, mode, createdAt, options: questionCounts });
  return buildSummary({ videoId, mode, createdAt, options: questionCounts }, 0);
}

function mockTitleSnippet(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length <= 60 ? clean : `${clean.slice(0, 59).trimEnd()}…`;
}

function mockCountWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

export async function mockCreateTextVideo(
  text: string,
  questionCounts?: QuizCounts,
  paid?: boolean,
): Promise<VideoSummary> {
  const words = mockCountWords(text);
  if (words < MOCK_TEXT_MIN_WORDS) {
    throw apiError(
      400,
      "TEXT_TOO_SHORT",
      `Add at least ${MOCK_TEXT_MIN_WORDS} words so there is enough material to quiz.`,
    );
  }
  if (words > MOCK_TEXT_WORDS_ACCEPTED) {
    throw apiError(
      422,
      "TEXT_TOO_LONG",
      `That is ${words} words; the maximum we accept is ${MOCK_TEXT_WORDS_ACCEPTED}. Trim it down and try again.`,
    );
  }
  const budget = paid ? MOCK_TEXT_WORDS_PAID : MOCK_TEXT_WORDS_FREE;
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "")
      : Math.random().toString(16).slice(2).padEnd(10, "0");
  const videoId = `text-${rand.slice(0, 10)}`;
  const createdAt = Date.now();
  JOBS.set(videoId, {
    videoId,
    mode: "text",
    createdAt,
    options: questionCounts,
    title: mockTitleSnippet(text),
    truncated: words > budget,
  });
  return buildSummary(JOBS.get(videoId)!, 0);
}

export async function mockSubmitTranscript(
  videoId: string,
  submission: { segments?: unknown[]; text?: string; language?: string | null; source?: string },
): Promise<VideoSummary> {
  const job = requireJob(videoId);
  job.transcriptSubmitted = true;
  job.errorCode = null;
  job.errorMessage = null;
  if (submission.text && submission.text.trim()) {
    job.title = mockTitleSnippet(submission.text);
  }
  return buildSummary(job, Date.now() - job.createdAt);
}

export async function mockGetVideo(videoId: string): Promise<VideoSummary> {
  const job = requireJob(videoId);
  return buildSummary(job, Date.now() - job.createdAt);
}

export async function mockGetQuiz(videoId: string, level: QuizLevel): Promise<PublicQuiz> {
  const job = requireJob(videoId);
  requireLevelReady(job, level);
  requireUnlocked(job, level);
  const { questions, count } = servedQuestions(job, level);
  return {
    id: `q_${videoId}_${level}`,
    videoId,
    level,
    model: MOCK_MODEL,
    createdAt: new Date(job.createdAt + MOCK_ELAPSED_TO_READY_MS).toISOString(),
    requestCount: count,
    questions: questions.map((q, i) => ({
      id: `mockq${level}_${i + 1}`,
      question: q.question,
      options: q.options,
      ...(q.scope ? { scope: q.scope } : {}),
    })),
  };
}

export async function mockSubmitAttempt(
  videoId: string,
  answers: number[],
  level: QuizLevel,
): Promise<QuizAttemptResult> {
  const job = requireJob(videoId);
  requireLevelReady(job, level);
  requireUnlocked(job, level);
  const { questions, count } = servedQuestions(job, level);
  const total = count;
  const results = questions.map((q, i) => {
    const selected = answers[i] ?? -1;
    const correctIndex = q.correctIndex;
    return {
      questionId: `mockq${level}_${i + 1}`,
      selected,
      correctIndex,
      isCorrect: selected === correctIndex,
      explanation: q.explanation,
    };
  });
  const score = results.filter((r) => r.isCorrect).length;
  const percent = Math.round((score / total) * 100);
  ATTEMPTS.set(attemptKey(videoId, level), Math.max(bestPercent(videoId, level) ?? 0, percent));
  return {
    attemptId: `a_${videoId}_${level}_${Date.now()}`,
    level,
    score,
    total,
    percent,
    passed: percent >= 70,
    results,
  };
}

export async function mockGetConfig(): Promise<PublicConfig> {
  return {
    model: MOCK_MODEL,
    questionCount: DEFAULT_COUNTS[1],
    maxTranscriptChars: 60_000,
    maxQuestionsPerAttempt: 50,
    minQuestionsPerLevel: 5,
    maxQuestionsPerLevel: MOCK_POOL_SIZE,
    poolSize: MOCK_POOL_SIZE,
    editRequiresPayment: MOCK_PAYWALL,
    minTextWords: MOCK_TEXT_MIN_WORDS,
    maxTextWordsFree: MOCK_TEXT_WORDS_FREE,
    maxTextWordsPaid: MOCK_TEXT_WORDS_PAID,
    maxTextWordsAccepted: MOCK_TEXT_WORDS_ACCEPTED,
    levels: [
      { level: 1, name: "Recap", tagline: "Straight from the video", questionCount: DEFAULT_COUNTS[1], outOfScopeCount: 0 },
      { level: 2, name: "Understand", tagline: "Apply what you learned", questionCount: DEFAULT_COUNTS[2], outOfScopeCount: 0 },
      { level: 3, name: "Beyond", tagline: "Go further than the video", questionCount: DEFAULT_COUNTS[3], outOfScopeCount: 3 },
    ],
  };
}

/** Toggle to test the paywalled edit path in mock/dev mode. */
export function setMockPaywall(enabled: boolean): void {
  MOCK_PAYWALL = enabled;
}

export function setMockRequirePreviousPass(enabled: boolean): void {
  MOCK_REQUIRE_PREVIOUS_PASS = enabled;
}

/** Dev/testing helper: force a mock job into the needs-browser fallback state. */
export function setMockNeedsBrowser(videoId: string): void {
  const job = JOBS.get(videoId);
  if (!job) return;
  job.errorCode = "TRANSCRIPT_NEEDS_BROWSER";
  job.errorMessage = null;
}

export async function mockUpdateQuizOptions(
  videoId: string,
  questionCounts: QuizCounts,
  paid?: boolean,
): Promise<QuizCounts> {
  if (MOCK_PAYWALL && paid !== true) {
    throw apiError(402, "PAYMENT_REQUIRED", "A paid plan is required to change question counts.");
  }
  const job = requireJob(videoId);
  job.options = questionCounts;
  return { ...questionCounts };
}

const MOCK_HISTORY_SEED_DAYS = [0.04, 0.5, 1.2, 2.7, 4.1, 6.5];

/** A demo library so the Browse page is populated without a real backend. */
function seedHistoryJobs(): void {
  const titles = [
    ["Cloudflare Workers: Serverless at the Edge", "Cloudflare"],
    ["D1 in Five Minutes: SQLite at the Edge", "Cloudflare Developers"],
    ["Rethinking State with Durable Objects", "Cloudflare TV"],
    ["How V8 Isolates Make Workers Fast", "The Primeagen"],
    ["R2 vs S3: Egress-Free Object Storage", "Fireship"],
    ["Building a Queue Consumer on Workers", "Cloudflare Developers"],
  ];
  titles.forEach(([title, channel], i) => {
    const videoId = `demo${(i + 1).toString().padStart(7, "0")}`;
    if (JOBS.has(videoId)) return;
    JOBS.set(videoId, {
      videoId,
      mode: "watch",
      createdAt: Date.now() - MOCK_HISTORY_SEED_DAYS[i]! * 86_400_000,
      title: title!,
      channel: channel!,
      transcriptSubmitted: true,
    });
  });
}
seedHistoryJobs();

function historyLevels(job: MockJob): HistoryLevelInfo[] {
  const counts = countsFor(job);
  const names = ["Recap", "Understand", "Beyond"];
  const taglines = ["Straight from the video", "Apply what you learned", "Go further than the video"];
  return ([1, 2, 3] as QuizLevel[]).map((level) => ({
    level,
    name: names[level - 1]!,
    tagline: taglines[level - 1]!,
    questionCount: counts[level],
  }));
}

function toHistoryItem(job: MockJob): VideoHistoryItem {
  const levels = historyLevels(job);
  const isText = job.mode === "text";
  return {
    videoId: job.videoId,
    mode: job.mode,
    isText,
    title: job.title ?? "Cloudflare Workers: Serverless at the Edge",
    channel: isText ? null : (job.channel ?? "Cloudflare"),
    thumbnail: isText ? null : `https://i.ytimg.com/vi/${job.videoId}/hqdefault.jpg`,
    durationSeconds: isText ? null : 1560,
    createdAt: new Date(job.createdAt * 1000).toISOString(),
    levels,
    totalQuestions: levels.reduce((sum, l) => sum + l.questionCount, 0),
  };
}

export async function mockGetHistory(query: {
  search?: string;
  limit?: number;
  cursor?: string;
}): Promise<VideoHistoryPage> {
  const limit = query.limit ?? 12;
  const search = query.search?.trim().toLowerCase();
  const ready = [...JOBS.values()]
    .filter((job) => {
      const instant = job.mode === "text" || job.transcriptSubmitted === true;
      const readyNow =
        !job.errorCode && (instant || Date.now() - job.createdAt >= MOCK_ELAPSED_TO_READY_MS);
      return readyNow;
    })
    .filter((job) => {
      if (!search) return true;
      const title = (job.title ?? "Cloudflare Workers: Serverless at the Edge").toLowerCase();
      const channel = (job.channel ?? "Cloudflare").toLowerCase();
      return title.includes(search) || channel.includes(search);
    })
    .sort((a, b) => b.createdAt - a.createdAt || b.videoId.localeCompare(a.videoId));

  let start = 0;
  if (query.cursor) {
    const sep = query.cursor.indexOf(":");
    const cursorAt = sep > 0 ? Number.parseInt(query.cursor.slice(0, sep), 10) : NaN;
    const cursorId = sep > 0 ? query.cursor.slice(sep + 1) : "";
    if (Number.isFinite(cursorAt)) {
      const idx = ready.findIndex(
        (job) => job.createdAt < cursorAt || (job.createdAt === cursorAt && job.videoId < cursorId),
      );
      start = idx === -1 ? ready.length : idx;
    }
  }

  const windowRows = ready.slice(start, start + limit + 1);
  const hasMore = windowRows.length > limit;
  const pageRows = hasMore ? windowRows.slice(0, limit) : windowRows;
  const last = pageRows[pageRows.length - 1];
  return {
    items: pageRows.map(toHistoryItem),
    hasMore,
    nextCursor: hasMore && last ? `${last.createdAt}:${last.videoId}` : null,
    total: ready.length,
  };
}

export async function mockGetHealth(): Promise<HealthReport> {
  return {
    status: "ok",
    service: "vid-quiz-api",
    version: "mock",
    time: new Date().toISOString(),
    uptime: 0,
    checks: { db: "configured", openrouter: "configured" },
  };
}

export async function mockGetDiagnostics(): Promise<DiagnosticsLog> {
  return { startedAt: 0, events: [] };
}